import React from 'react';

import * as benchlingUtil from 'BenchlingUtil';
import BenchLink from 'link-preview/BenchLink';

import {
  RelevantItemsGraphQL_RelevantItemsNode_workflows_WorkflowRelevantItemNode as RelevantWorkflowGQL,
  RelevantItemsGraphQL_RelevantItemsNode_workflows_WorkflowRelevantItemNode_stageEntriesMentioning_StageEntryMentioningNode as StageEntryMentioningGQL,
} from './__generated/RelevantItemsGraphQL';

interface RelevantWorkflowInfo {
  description: JSX.Element;
  modifiedAt: string;
  name: string;
  projectName: string;
}

function getSamplesText(stageEntriesMentioning: Array<StageEntryMentioningGQL>): string {
  // If there is only one sample, show the name. Otherwise, show the number of distinct samples.
  const stageEntries = stageEntriesMentioning
    .map(({inputSamples, registeredSamples}) => [...inputSamples, ...registeredSamples])
    .flat()
    .map(({name}): string => name!);
  const sampleNames = [...new Set(stageEntries)];
  return sampleNames.length > 1 ? `${sampleNames.length} samples` : sampleNames[0];
}

export default function getRelevantWorkflowInfo(
  relevantWorkflow: RelevantWorkflowGQL,
  areConvertedRelevantItems: boolean = false
): RelevantWorkflowInfo {
  const {workflow, stageEntriesMentioning} = relevantWorkflow;
  let description;
  // If there is a single stage entry of interest (only one exists, only one with registered samples
  // exists), show a link to the entry.
  const stageEntriesWithRegisteredSamples = stageEntriesMentioning.filter(
    ({registeredSamples}) => registeredSamples.length > 0
  );
  let singleStageEntryMentioning;
  if (stageEntriesMentioning.length === 1) {
    singleStageEntryMentioning = stageEntriesMentioning[0];
  } else if (stageEntriesWithRegisteredSamples.length === 1) {
    singleStageEntryMentioning = stageEntriesWithRegisteredSamples[0];
  }
  if (singleStageEntryMentioning) {
    const {stageEntry} = singleStageEntryMentioning;
    description = (
      <span>
        {getSamplesText([singleStageEntryMentioning])} referenced in{' '}
        <BenchLink href={benchlingUtil.pathToURL(stageEntry.editUrl)} fileId={stageEntry.id}>
          {stageEntry.name}
        </BenchLink>
        {areConvertedRelevantItems ? ' on converted item' : ''}
      </span>
    );
    // Otherwise, show "multiple entries".
  } else {
    const samplesText = getSamplesText(stageEntriesMentioning);
    description = (
      <span>
        {samplesText} referenced in multiple entries{areConvertedRelevantItems ? ' on converted item' : ''}
      </span>
    );
  }
  let name = workflow.humanId;
  if (workflow.name) {
    name += ` \u2022 ${workflow.name}`;
  }

  return {
    description,
    modifiedAt: workflow.modifiedAt,
    name,
    projectName: workflow.source.name,
  };
}
